// API client with auto auth header injection
const API_BASE = import.meta.env.VITE_API_URL || '';

function getToken(): string | null {
  return localStorage.getItem('chat_token');
}

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    ...((options.headers as Record<string, string>) || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });

  if (res.status === 401) {
    localStorage.removeItem('chat_token');
    localStorage.removeItem('chat_user');
    window.location.reload();
    throw new Error('Unauthorized');
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Unknown error' })) as { error: string };
    throw new Error(err.error || `HTTP ${res.status}`);
  }

  return res.json() as Promise<T>;
}

// Auth
export const api = {
  auth: {
    googleLogin: (token: string) =>
      request<{ token: string; user: import('../types').User }>('/api/auth/google', {
        method: 'POST',
        body: JSON.stringify({ token }),
      }),
    mockLogin: (name?: string) =>
      request<{ token: string; user: import('../types').User }>('/api/auth/mock', {
        method: 'POST',
        body: JSON.stringify({ name }),
      }),
    logout: () =>
      request<{ message: string }>('/api/auth/logout', { method: 'POST' }),
    me: () =>
      request<{ user: import('../types').User }>('/api/auth/me'),
  },

  rooms: {
    list: () =>
      request<{ rooms: import('../types').Room[] }>('/api/rooms'),
    get: (id: string) =>
      request<{ room: import('../types').Room }>(`/api/rooms/${id}`),
    create: (data: {
      type: 'personal' | 'group';
      name?: string;
      description?: string;
      memberIds: string[];
    }) =>
      request<{ room: import('../types').Room; existing?: boolean }>('/api/rooms', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    addMember: (roomId: string, userId: string) =>
      request<{ message: string }>(`/api/rooms/${roomId}/members`, {
        method: 'POST',
        body: JSON.stringify({ userId }),
      }),
    removeMember: (roomId: string, userId: string) =>
      request<{ message: string }>(`/api/rooms/${roomId}/members/${userId}`, {
        method: 'DELETE',
      }),
  },

  messages: {
    list: (roomId: string, before?: string, limit = 50) => {
      const params = new URLSearchParams({ limit: String(limit) });
      if (before) params.set('before', before);
      return request<{
        messages: import('../types').Message[];
        hasMore: boolean;
        nextCursor: string | null;
      }>(`/api/rooms/${roomId}/messages?${params}`);
    },
  },

  users: {
    search: (q: string) =>
      request<{ users: import('../types').User[] }>(`/api/users/search?q=${encodeURIComponent(q)}`),
    get: (id: string) =>
      request<{ user: import('../types').User }>(`/api/users/${id}`),
    registerFcmToken: (token: string) =>
      request<{ success: boolean }>('/api/users/fcm-token', {
        method: 'POST',
        body: JSON.stringify({ token }),
      }),
    unregisterFcmToken: (token: string) =>
      request<{ success: boolean }>('/api/users/fcm-token', {
        method: 'DELETE',
        body: JSON.stringify({ token }),
      }),
  },

  files: {
    upload: async (file: File): Promise<{ key: string; url: string; name: string; size: number; type: string }> => {
      const token = getToken();
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${API_BASE}/api/files/upload`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Upload failed' })) as { error: string };
        throw new Error(err.error);
      }

      return res.json();
    },
  },
};

export function getWsUrl(roomId: string, token: string): string {
  const apiBase = import.meta.env.VITE_API_URL || '';
  const wsBase = apiBase.replace('https://', 'wss://').replace('http://', 'ws://') || `ws://${window.location.host}`;
  return `${wsBase}/api/ws/${roomId}?token=${encodeURIComponent(token)}`;
}
